import { buildFormulaScope, preprocessAbsNotation, evaluateFormulaRaw, type AngleMode } from '@agavi/blocks';
import { evaluate } from 'mathjs';

export type { AngleMode };

// `evaluateFormulaRaw` moved to packages/blocks alongside computeFormulaExtrema, which calls it
// tens of thousands of times per search and must not depend on an app module. Re-exported here so
// the app's existing importers keep working and there is still exactly ONE implementation.
export { evaluateFormulaRaw };

// buildFormulaScope (teacher-friendly log/ln/arcsin-alias/angle-mode-aware-trig overrides) and
// preprocessAbsNotation (|expr| → abs(expr)) now live in packages/blocks/formula-scope.ts — the
// ONE shared implementation every formula input in the app evaluates through (this file's own
// preview/evaluate helpers, numerical.ts's Math Error candidate generation, and dropdown.ts's
// evaluateEquation), so a formula can never evaluate differently depending on which surface
// happens to run it. Previously duplicated here.

function fmtEval(n: number): string {
  const abs = Math.abs(n);
  const notation: Intl.NumberFormatOptions['notation'] =
    n !== 0 && (abs < 1e-5 || abs >= 1e5) ? 'scientific' : 'standard';
  return new Intl.NumberFormat(undefined, { notation, maximumSignificantDigits: 6 }).format(n).replace(/E\+?/, 'e');
}

function buildScope(values: Record<string, string>, angleMode: AngleMode): Record<string, unknown> {
  const scope: Record<string, unknown> = { ...buildFormulaScope(angleMode) };
  for (const [k, v] of Object.entries(values)) {
    const n = parseFloat(v);
    if (!isNaN(n)) scope[k] = n;
  }
  return scope;
}

/**
 * Evaluates a numeric expression string with variable substitution.
 * Returns a formatted string on success, or Error on invalid/empty input.
 */
export function evaluateFormula(
  expr: string,
  values: Record<string, string>,
  angleMode: AngleMode = 'radians',
): string | Error {
  const trimmed = expr.trim();
  if (!trimmed) return new Error('empty');
  try {
    const resolved = preprocessAbsNotation(trimmed.replace(/\{([^}]+)\}/g, '$1'));
    const result = evaluate(resolved, buildScope(values, angleMode));
    if (typeof result !== 'number' || !isFinite(result)) return new Error('not a number');
    return fmtEval(result);
  } catch {
    return new Error('parse error');
  }
}
